import { PkAvatar, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ComponentProps, HTMLAttributeAnchorTarget, MouseEvent, ReactNode } from 'react';
import { Fragment, memo, useCallback, useMemo } from 'react';

import TeamBadge from '../TeamBadge';

import { getPlayerUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import type { Player, RoundPhase, Team } from '@shared-types/match.types';

export type PlayerIdentifierProps = ComponentProps<typeof PkIdentifier> & {
  avatarClassName?: string;
  badgeClassName?: string;
  player: Player;
  team?: Team;
  useFullName?: boolean;
  showTeamName?: boolean;
  showPosition?: boolean;
  avatarSize?: number;
  badgeSize?: number;
  customSecondaryText?: string | ReactNode;
  displayOrder?: 'inverted' | 'normal';
  badgeSlot?: 'prefix' | 'suffix';
  backgroundColor?: string;
  competitionId?: string;
  phase?: RoundPhase;
  onIdentifierSelected?: (
    event?: CustomEvent<{
      element: HTMLElement;
      selected: boolean;
    }>,
  ) => void;
  enableLink?: boolean;
  linkTarget?: HTMLAttributeAnchorTarget;
  linkSubPage?: string;
  wrapperClass?: string;
  onLinkClick?: (e: MouseEvent) => void;
};

const PlayerIdentifier = (props: PlayerIdentifierProps) => {
  const {
    className = 'pk-py--xs',
    avatarClassName,
    badgeClassName,
    player,
    team,
    type = 'horizontal',
    useFullName = false,
    showTeamName = true,
    showPosition = true,
    avatarSize = 48,
    badgeSize = 16,
    customSecondaryText,
    backgroundColor,
    displayOrder = 'normal',
    badgeSlot = 'prefix',
    rounded = false,
    competitionId = window.competitionId,
    phase = window.currentPhase,
    onIdentifierSelected,
    wrapperClass = '',
    enableLink = false,
    linkTarget = '_self',
    linkSubPage = '',
    onLinkClick,
    children,
  } = props;
  const hydratedPlayer = useMemo(() => hydrateWithTranslations(player), [player]);

  const primaryText = useMemo(
    () => (useFullName ? hydratedPlayer?.name : hydratedPlayer?.shortName) ?? '',
    [hydratedPlayer, useFullName],
  );

  const secondaryText = useMemo(() => {
    if (customSecondaryText) return customSecondaryText;

    let text = '';
    if (showTeamName) text += hydrateWithTranslations(team)?.displayName ?? '';
    if (text && showPosition && hydratedPlayer?.fieldPosition) text += ' - ';
    if (showPosition && hydratedPlayer?.fieldPosition) text += hydratedPlayer?.fieldPosition;

    return text;
  }, [customSecondaryText, hydratedPlayer?.fieldPosition, showPosition, showTeamName, team]);

  const onAvatarClick = useCallback(() => onIdentifierSelected(), [onIdentifierSelected]);

  const handleLinkClick = useCallback(
    (e: MouseEvent) => {
      if (e.button > 1) return; // ignore right-clicks (context menu)
      onLinkClick?.(e);
    },
    [onLinkClick],
  );

  const link = useMemo(() => {
    if (!enableLink || !player?.id) return null;
    return getPlayerUrl(player?.id, window.competitionId, phase, linkSubPage);
  }, [enableLink, linkSubPage, phase, player?.id]);

  const hasLink = link && link !== '#';
  const Tag = hasLink ? 'a' : Fragment;

  const linkProps = useMemo(
    () =>
      hasLink
        ? {
            className: wrapperClass,
            title: hydrateWithTranslations(player)?.displayName,
            href: link,
            target: linkTarget,
            onClick: handleLinkClick,
            onAuxClick: handleLinkClick,
          }
        : {},
    [handleLinkClick, hasLink, link, linkTarget, player, wrapperClass],
  );

  return (
    <Tag {...linkProps}>
      <PkIdentifier
        className={clsx('pk-identifier', className)}
        onPkChange={onIdentifierSelected}
        type={type}
        order={displayOrder}
        rounded={rounded}
        bgColor={backgroundColor}
        {...props}
      >
        <PkAvatar
          slot={badgeSlot}
          className={clsx(avatarClassName, 'pk-avatar')}
          type='image'
          src={player?.imageUrl}
          size={avatarSize}
          onPkAvatarClick={onAvatarClick} // firefox doesn't register click without handler
        >
          {!!team && (
            <TeamBadge
              className={clsx(badgeClassName, 'pk-badge')}
              team={team}
              size={badgeSize}
              slot='badge'
              competitionId={competitionId}
              phase={phase}
            />
          )}
        </PkAvatar>
        <span slot='primary'>{primaryText}</span>
        {!!secondaryText && <span slot='secondary'>{secondaryText}</span>}
        {children}
      </PkIdentifier>
    </Tag>
  );
};

export default memo(PlayerIdentifier);
